'use client';

import { useRef, useState } from 'react';
import { Link } from '@inertiajs/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { login, register } from '@/routes';
import AppLogoIcon from '@/components/app-logo-icon';
import { T } from './V2Tokens';
import { useIsMobile } from './useIsMobile';

gsap.registerPlugin(ScrollTrigger, useGSAP);

const LINKS = [
    { label: 'أقسام المنصة', href: '#divisions' },
    { label: 'آلية العمل',   href: '#process' },
    { label: 'أعمالنا',      href: '#portfolio' },
    { label: 'الأسعار',      href: '#pricing' },
    { label: 'أسئلة شائعة',  href: '#faq' },
];

// Docked nav height — used to offset sticky sections in other components
export const NAV_H = 64;

export function V2Nav() {
    const isMobile = useIsMobile();
    const [menuOpen, setMenuOpen] = useState(false);
    const innerRef = useRef<HTMLElement>(null);

    useGSAP(() => {
        if (isMobile || !innerRef.current) return;
        ScrollTrigger.create({
            start: '80px top',
            onEnter:     () => gsap.to(innerRef.current, { boxShadow: '0 1px 16px rgba(14,11,46,0.10)', duration: 0.3, ease: 'power2.out' }),
            onLeaveBack: () => gsap.to(innerRef.current, { boxShadow: 'none', duration: 0.3, ease: 'power2.out' }),
        });
    }, [isMobile]);

    // ── Mobile nav ────────────────────────────────────────────────────────────
    if (isMobile) {
        return (
            <>
                {/* Fixed mobile bar */}
                <div
                    style={{
                        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
                        height: NAV_H,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '0 20px',
                        background: 'rgba(237,234,251,0.92)',
                        backdropFilter: 'blur(20px) saturate(160%)',
                        WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                        borderBottom: `1px solid ${T.line}`,
                        boxSizing: 'border-box',
                    }}
                    dir="rtl"
                >
                    {/* Logo icon only */}
                    <Link href="/" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
                        <div style={{ width: 30, height: 30, borderRadius: 8, background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <AppLogoIcon color="white" style={{ width: 17, height: 17 }} />
                        </div>
                    </Link>

                    {/* Hamburger — on the left in RTL */}
                    <button
                        onClick={() => setMenuOpen(true)}
                        aria-label="القائمة"
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: 8, display: 'flex', flexDirection: 'column',
                            gap: 5, alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <span style={{ display: 'block', width: 22, height: 2, background: T.ink, borderRadius: 1 }} />
                        <span style={{ display: 'block', width: 22, height: 2, background: T.ink, borderRadius: 1 }} />
                        <span style={{ display: 'block', width: 22, height: 2, background: T.ink, borderRadius: 1 }} />
                    </button>
                </div>

                {/* Fullscreen overlay menu */}
                {menuOpen && (
                    <div
                        style={{
                            position: 'fixed', inset: 0, zIndex: 100,
                            background: T.bgWhite,
                            display: 'flex', flexDirection: 'column',
                            padding: '20px 24px 40px',
                            boxSizing: 'border-box',
                            overflowY: 'auto',
                        }}
                        dir="rtl"
                    >
                        {/* Top row: logo + close */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 40 }}>
                            <Link
                                href="/"
                                onClick={() => setMenuOpen(false)}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', color: T.ink }}
                            >
                                <div style={{ width: 30, height: 30, borderRadius: 8, background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <AppLogoIcon color="white" style={{ width: 17, height: 17 }} />
                                </div>
                                <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.025em' }}>مجرّد استديو</span>
                            </Link>
                            <button
                                onClick={() => setMenuOpen(false)}
                                aria-label="إغلاق القائمة"
                                style={{
                                    background: 'none', border: `1px solid ${T.line}`,
                                    borderRadius: 99, width: 36, height: 36,
                                    cursor: 'pointer', fontSize: 20, color: T.ink,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >
                                ×
                            </button>
                        </div>

                        {/* Nav links */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                            {LINKS.map(l => (
                                <a
                                    key={l.label}
                                    href={l.href}
                                    onClick={() => setMenuOpen(false)}
                                    style={{
                                        fontSize: 22, fontWeight: 700, color: T.ink,
                                        textDecoration: 'none', padding: '14px 0',
                                        borderBottom: `1px solid ${T.line}`,
                                    }}
                                >
                                    {l.label}
                                </a>
                            ))}
                        </div>

                        {/* CTAs */}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 40 }}>
                            <Link
                                href={register.url()}
                                style={{
                                    display: 'block', textAlign: 'center', textDecoration: 'none',
                                    padding: '15px 0', borderRadius: T.rPill,
                                    background: T.ink, color: '#fff',
                                    fontSize: 16, fontWeight: 700,
                                }}
                            >
                                ابدأ مجاناً ←
                            </Link>
                            <Link
                                href={login.url()}
                                style={{
                                    display: 'block', textAlign: 'center', textDecoration: 'none',
                                    padding: '14px 0', borderRadius: T.rPill,
                                    background: 'transparent', color: T.ink,
                                    border: `1px solid ${T.ink20}`,
                                    fontSize: 16, fontWeight: 600,
                                }}
                            >
                                تسجيل الدخول
                            </Link>
                        </div>
                    </div>
                )}
            </>
        );
    }

    // ── Desktop nav ───────────────────────────────────────────────────────────
    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50 }}>
            <nav
                ref={innerRef}
                style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '18px 56px',
                    borderBottom: `1px solid ${T.line}`,
                    background: 'rgba(237,234,251,0.92)',
                    backdropFilter: 'blur(28px) saturate(170%)',
                    WebkitBackdropFilter: 'blur(28px) saturate(170%)',
                    boxSizing: 'border-box',
                }}
                dir="rtl"
            >
                {/* Logo */}
                <Link
                    href="/"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: T.ink, textDecoration: 'none', flexShrink: 0 }}
                >
                    <div style={{ width: 30, height: 30, borderRadius: 8, background: T.ink, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <AppLogoIcon color="white" style={{ width: 17, height: 17 }} />
                    </div>
                    <span style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.025em', whiteSpace: 'nowrap' }}>
                        مجرّد استديو
                    </span>
                </Link>

                {/* Nav links */}
                <div style={{ display: 'flex', gap: 28, fontSize: 14, fontWeight: 500, color: T.ink70 }}>
                    {LINKS.map(l => (
                        <a
                            key={l.label}
                            href={l.href}
                            style={{ color: 'inherit', textDecoration: 'none', whiteSpace: 'nowrap', transition: 'color .15s' }}
                            onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.color = T.ink; }}
                            onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.color = 'inherit'; }}
                        >
                            {l.label}
                        </a>
                    ))}
                </div>

                {/* CTAs */}
                <div style={{ display: 'flex', gap: 8, flexShrink: 0, alignItems: 'center' }}>
                    <Link
                        href={login.url()}
                        style={{
                            padding: '10px 18px', borderRadius: T.rPill,
                            background: 'transparent', color: T.ink,
                            border: `1px solid ${T.ink20}`,
                            fontSize: 13, fontWeight: 700, textDecoration: 'none',
                            whiteSpace: 'nowrap',
                            transition: 'border-color .15s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = T.ink70; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = T.ink20; }}
                    >
                        تسجيل الدخول
                    </Link>
                    <Link
                        href={register.url()}
                        style={{
                            padding: '10px 18px', borderRadius: T.rPill,
                            background: T.ink, color: '#fff',
                            fontSize: 13, fontWeight: 700, textDecoration: 'none',
                            whiteSpace: 'nowrap',
                            transition: 'opacity .15s',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.85'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                    >
                        ابدأ مجاناً ←
                    </Link>
                </div>
            </nav>
        </div>
    );
}
